'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuMinus, LuPlus } from 'react-icons/lu'
import { Box, Flex, Text } from '@chakra-ui/react'
import { tryParseAsNumber } from '@leid/helpers'

import { CartFormValues } from 'appTypes/orders'
import { Input } from 'components/form/Input'
import { RectButton } from 'components/ui/Buttons/RectButton'

type Props = {
  values: CartFormValues
  onAmountChange: (amount: number) => void
}

const AmountSelector = ({ values, onAmountChange }: Props): JSX.Element => {
  const { t } = useTranslation('common')

  const [amount, setAmount] = useState<number | undefined>(values.amount)
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    setError(undefined)
    setAmount(values.amount)
  }, [values.amount])

  const isMinusDisabled = (): boolean => {
    if (values.amount <= 1) {
      return true
    }
    return false
  }

  const changeAmount = (diff: number) => {
    onAmountChange(values.amount + diff)
  }

  const handleAmountChange = (newAmount: string) => {
    const amountInt = tryParseAsNumber(newAmount)
    setAmount(amountInt)

    if (amountInt === undefined) {
      setError(t('validationNumber'))
      return
    }

    if (amountInt < 1) {
      // TODO: Translate
      setError(`At least 1 should be selected`)
      return
    }

    setError(undefined)
    onAmountChange(amountInt)
  }

  return (
    <Flex alignItems="center" flexDirection="row" gap={5}>
      <Text>{t('Quantity')}: </Text>

      <Flex height="24px" alignItems="center" className="gtm-change-quantity-wrapper">
        <RectButton isDisabled={isMinusDisabled()} onClick={() => changeAmount(-1)} type="button">
          <Box color="green.a200">
            <LuMinus size={18} />
          </Box>
        </RectButton>

        <Flex direction="column" marginX="10px">
          <Input
            value={amount?.toString() ?? ''}
            onChange={(val) => handleAmountChange(val.target.value)}
            name="amount"
            touched={error !== undefined}
            error={error}
          />
        </Flex>

        <RectButton onClick={() => changeAmount(1)} type="button">
          <Box color="green.a200">
            <LuPlus size={18} />
          </Box>
        </RectButton>
      </Flex>
    </Flex>
  )
}

export default AmountSelector
